import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@heroui/react";

import { store, useAppSelector } from "@/app/store";
import BreadCrumbs from "@/components/Common/BreadCrumbs";
import FlexRow from "@/components/Common/FlexRow";
import Grid from "@/components/Common/Grid";
import Tabs from "@/components/Common/Tabs";
import { FieldInfo } from "@/components/Common/Dropdown";

import Projects from "@/pages/Projects/Projects";

interface PlatformRow {
  platform: string;
  variant: string;
  count: number;
}

interface ChannelRow {
  epg_id: string;
  name: string;
  project_count: number;
}

// Purely decorative — platforms are free-form strings reported by devices,
// so anything unrecognised falls back to a generic icon.
const platformIcon = (platform: string): string => {
  const p = platform.toLowerCase();
  if (p.includes('android')) return 'android';
  if (p.includes('desktop')) return 'desktop_windows';
  if (p.includes('webos') || p.includes('tizen') || p.includes('tv')) return 'tv';
  return 'devices';
};

const Platform = () => {
  const { t } = useTranslation();
  const { tab, platform } = useParams();
  const location = useLocation();
  const projects = useAppSelector((state) => state.projects);
  const stats = useAppSelector((state) => state.stats);
  const navigate = useNavigate();

  const isSearchProject = location.pathname.endsWith('/searchproject');

  const [platformCols, setPlatformCols] = useState<any[]>([]);
  const [platformRows, setPlatformRows] = useState<PlatformRow[]>([]);
  const [channelCols, setChannelCols] = useState<any[]>([]);
  const [channelRows, setChannelRows] = useState<ChannelRow[]>([]);
  const [channelsLoading, setChannelsLoading] = useState<boolean>(false);

  const active = tab || "statistics";

  const tabs: FieldInfo[] = useMemo(() => [
    { value: "statistics", desc: "sidebar:settings:statistics" },
    { value: "channels", desc: "channels:channels" },
  ], []);

  // Fetch stats data when channels tab is active
  useEffect(() => {
    const fetchData = async () => {
      if (active === "channels") {
        // Only show loading if no data exists
        if (!stats.channels || stats.channels.length === 0) {
          setChannelsLoading(true);
        }
        await store.getStatsData();
        setChannelsLoading(false);
      }
    };
    fetchData();
  }, [active]);

  // Platform drill-down: fetch the projects that have a device of this
  // platform. Clear first so stale rows from a previous drill-down never flash.
  useEffect(() => {
    if (isSearchProject && platform) {
      store.clearChannelListProjects();
      store.getProjectsByPlatform(platform);
    }
  }, [isSearchProject, platform]);

  // Define columns for platform grid
  useEffect(() => {
    setPlatformCols([
      {
        field: "platform",
        headerName: t("projects:platforms:platform"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "variant",
        headerName: t("projects:platforms:variant"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "count",
        headerName: t("projects:platforms:count"),
        flex: 1,
        minWidth: 100,
      },
    ]);
  }, [t]);

  // Define columns for channel grid
  useEffect(() => {
    setChannelCols([
      {
        field: "epg_id",
        headerName: t("channels:grid:epg_id"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "name",
        headerName: t("channels:grid:name"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "project_count",
        headerName: t("channels:grid:project_count"),
        flex: 1,
        minWidth: 100,
      },
    ]);
  }, [t]);

  // Flatten platforms and variants into rows
  const flattenedPlatformRows = useMemo(() => {
    const result: PlatformRow[] = [];
    const platforms = projects.stats?.platforms || [];

    for (const platform of platforms) {
      if (platform.variants && platform.variants.length > 0) {
        for (const variant of platform.variants) {
          result.push({
            platform: platform.platform || "-",
            variant: variant.variant || "-",
            count: variant.count,
          });
        }
      } else {
        result.push({
          platform: platform.platform || "-",
          variant: "-",
          count: platform.cumul,
        });
      }
    }
    return result;
  }, [projects.stats?.platforms]);

  useEffect(() => {
    setPlatformRows(flattenedPlatformRows);
  }, [flattenedPlatformRows]);

  // Set channel rows from stats slice
  useEffect(() => {
    setChannelRows(stats.channels || []);
  }, [stats.channels]);

  const updateActive = (act: string) => {
    const url = act === "statistics"
      ? `/settings/statistics`
      : `/settings/statistics/${act}`;
    navigate(url);
    console.log("Platform/Tabs/updateActive/act=" + act + "/url=" + url);
  };

  if (isSearchProject && platform) {
    return (
    <React.Fragment>
      <FlexRow direction="column"
               align="start"
               className="bg-white px-[32px] pt-[24px] pb-[8px] sticky top-[64px] z-10">
        <FlexRow flex={0}
                 justify="start"
                 className="gap-[12px]">
          <div className="shrink-0 whitespace-nowrap">
            <BreadCrumbs pages={[
              ['/settings/statistics', t('sidebar:settings:statistics')],
              ['', t('projects:platforms:stat_projects')],
            ]} />
          </div>
          <FlexRow flex={0}
                   width="auto"
                   className="shrink-0 gap-[6px] bg-[#EAF3EC] border border-[#C8DFCE] rounded-full px-[12px] py-[3px]">
            <span className="material-icons !text-[16px] !text-[#2E7D32]">
              { platformIcon(platform) }
            </span>
            <span className="text-[14px] font-medium text-[#2E7D32] leading-normal">
              { platform }
            </span>
          </FlexRow>
        </FlexRow>
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className="py-[16px] px-[32px]">
        <Projects hideContainer={true}
                  platformFilter={platform} />
      </FlexRow>
    </React.Fragment>
    );
  }

  return (
    <FlexRow direction="column" align="start" className="p-[24px] w-full">
      <FlexRow className="py-[20px] w-full">
        <Tabs tabs={tabs}
              flex={1}
              active={active}
              setActive={updateActive} />
      </FlexRow>

      {active === "statistics" && (
        <FlexRow className="w-full [&_.ag-header-cell]:!border-r-0 [&_.ag-cell]:!border-r-0" direction="column">
          <Grid
            columnDefs={platformCols}
            rowData={platformRows}
            maxHeight="calc(100vh - 250px)"
            showPages={true}
            pageSize={15}
            filter={true}
            onRowClicked={(event) => {
              const row = event.data as PlatformRow;
              // The API matches on the platform only, never the variant —
              // every variant row of one platform leads to the same list.
              if (row?.platform && row.platform !== "-")
                navigate(`/settings/statistics/${encodeURIComponent(row.platform)}/searchproject`);
            }}
          />
        </FlexRow>
      )}

      {active === "channels" && (
        <FlexRow className="w-full [&_.ag-header-cell]:!border-r-0 [&_.ag-cell]:!border-r-0" direction="column">
          {channelsLoading ? (
            <FlexRow className="w-full justify-center py-10">
              <CircularProgress aria-label="loading" />
            </FlexRow>
          ) : (
            <Grid
              columnDefs={channelCols}
              rowData={channelRows}
              maxHeight="calc(100vh - 250px)"
              showPages={true}
              pageSize={15}
              filter={true}
              onRowClicked={(event) => {
                const row = event.data as ChannelRow;
                if (row?.epg_id) {
                  navigate(`/channels/${encodeURIComponent(row.epg_id)}/searchproject`);
                }
              }}
            />
          )}
        </FlexRow>
      )}
    </FlexRow>
  );
};

export default Platform;
